import { useState, useEffect, useCallback, useRef } from 'react'
import { useCompany } from '@/hooks/useCompany'
import { brandingService } from '@/services/branding.service'
import type { CompanyBranding } from '@/types/branding.types'

export function applyBrandingCssVariables(branding: Partial<CompanyBranding> | null) {
  if (typeof document === 'undefined') return

  if (!branding) {
    removeBrandingCssVariables()
    return
  }

  const isDark = document.documentElement.getAttribute('data-theme') === 'dark'

  if (isDark) {
    // ── MODO ESCURO (Dark Mode Tokens) ──
    const primary = branding.dark_primary_color || (branding.primary_color ? undefined : '#C19E73')
    if (primary) document.documentElement.style.setProperty('--primary', primary)

    const primaryHover = branding.dark_primary_hover || '#E7C58E'
    if (primaryHover) document.documentElement.style.setProperty('--primary-hover', primaryHover)

    const primaryLight = branding.dark_primary_light || '#3D2B1C'
    if (primaryLight) document.documentElement.style.setProperty('--primary-light', primaryLight)

    const primaryDark = branding.dark_primary_dark || '#0A0A0A'
    if (primaryDark) document.documentElement.style.setProperty('--primary-dark', primaryDark)

    const primaryGlow = branding.dark_primary_glow || 'rgba(193, 158, 115, 0.25)'
    if (primaryGlow) document.documentElement.style.setProperty('--primary-glow', primaryGlow)

    const bg = branding.dark_background_color || '#0B0B0B'
    if (bg) document.documentElement.style.setProperty('--bg', bg)

    const surface = branding.dark_surface_color || '#121212'
    if (surface) document.documentElement.style.setProperty('--surface', surface)

    const surface2 = branding.dark_surface_secondary || '#1E1E1E'
    if (surface2) {
      document.documentElement.style.setProperty('--surface-2', surface2)
      document.documentElement.style.setProperty('--bg-subtle', surface2)
      document.documentElement.style.setProperty('--bg-2', surface2)
    }

    const surface3 = branding.dark_surface_tertiary || '#262626'
    if (surface3) document.documentElement.style.setProperty('--surface-3', surface3)

    const border = branding.dark_border_color || '#2D2D2D'
    if (border) document.documentElement.style.setProperty('--border', border)

    const borderStrong = branding.dark_border_strong || '#404040'
    if (borderStrong) document.documentElement.style.setProperty('--border-strong', borderStrong)

    const text1 = branding.dark_text_primary || '#FFFFFF'
    if (text1) {
      document.documentElement.style.setProperty('--text-1', text1)
      document.documentElement.style.setProperty('--text-main', text1)
    }

    const text2 = branding.dark_text_secondary || '#EAEAEA'
    if (text2) {
      document.documentElement.style.setProperty('--text-2', text2)
      document.documentElement.style.setProperty('--text-muted', text2)
    }

    const textOnPrimary = branding.dark_text_on_primary || '#121212'
    if (textOnPrimary) document.documentElement.style.setProperty('--text-on-primary', textOnPrimary)
  } else {
    // ── MODO CLARO (Light Mode Tokens) ──
    if (branding.primary_color) {
      document.documentElement.style.setProperty('--primary', branding.primary_color)
    }
    if (branding.primary_hover) {
      document.documentElement.style.setProperty('--primary-hover', branding.primary_hover)
    }
    if (branding.primary_light) {
      document.documentElement.style.setProperty('--primary-light', branding.primary_light)
    }
    if (branding.primary_dark) {
      document.documentElement.style.setProperty('--primary-dark', branding.primary_dark)
    }
    if (branding.primary_glow) {
      document.documentElement.style.setProperty('--primary-glow', branding.primary_glow)
    }

    if (branding.background_color) {
      document.documentElement.style.setProperty('--bg', branding.background_color)
    }
    if (branding.surface_color) {
      document.documentElement.style.setProperty('--surface', branding.surface_color)
    }
    if (branding.surface_secondary) {
      document.documentElement.style.setProperty('--surface-2', branding.surface_secondary)
      document.documentElement.style.setProperty('--bg-subtle', branding.surface_secondary)
      document.documentElement.style.setProperty('--bg-2', branding.surface_secondary)
    }
    if (branding.surface_tertiary) {
      document.documentElement.style.setProperty('--surface-3', branding.surface_tertiary)
    }

    if (branding.border_color) {
      document.documentElement.style.setProperty('--border', branding.border_color)
    }
    if (branding.border_strong) {
      document.documentElement.style.setProperty('--border-strong', branding.border_strong)
    }

    if (branding.text_primary) {
      document.documentElement.style.setProperty('--text-1', branding.text_primary)
      document.documentElement.style.setProperty('--text-main', branding.text_primary)
    }
    if (branding.text_secondary) {
      document.documentElement.style.setProperty('--text-2', branding.text_secondary)
      document.documentElement.style.setProperty('--text-muted', branding.text_secondary)
    }
    if (branding.text_on_primary) {
      document.documentElement.style.setProperty('--text-on-primary', branding.text_on_primary)
    }
  }
}

export function removeBrandingCssVariables() {
  if (typeof document === 'undefined') return
  document.documentElement.style.removeProperty('--primary')
  document.documentElement.style.removeProperty('--primary-hover')
  document.documentElement.style.removeProperty('--primary-light')
  document.documentElement.style.removeProperty('--primary-dark')
  document.documentElement.style.removeProperty('--primary-glow')
  document.documentElement.style.removeProperty('--bg')
  document.documentElement.style.removeProperty('--surface')
  document.documentElement.style.removeProperty('--surface-2')
  document.documentElement.style.removeProperty('--bg-subtle')
  document.documentElement.style.removeProperty('--bg-2')
  document.documentElement.style.removeProperty('--surface-3')
  document.documentElement.style.removeProperty('--border')
  document.documentElement.style.removeProperty('--border-strong')
  document.documentElement.style.removeProperty('--text-1')
  document.documentElement.style.removeProperty('--text-main')
  document.documentElement.style.removeProperty('--text-2')
  document.documentElement.style.removeProperty('--text-muted')
  document.documentElement.style.removeProperty('--text-on-primary')
}


export function useBranding() {
  let companyId: string | undefined = undefined
  let parentWhitelabelId: string | null | undefined = undefined
  try {
    const companyCtx = useCompany()
    const activeCompany = companyCtx?.activeCompany
    companyId = activeCompany?.id
    parentWhitelabelId = activeCompany?.parent_whitelabel_id
  } catch {
    companyId = undefined
    parentWhitelabelId = undefined
  }

  const [branding, setBranding] = useState<CompanyBranding | null>(() => {
    if (!companyId || typeof window === 'undefined') return null
    try {
      const cached = sessionStorage.getItem(`branding_${companyId}`) ||
        (parentWhitelabelId ? sessionStorage.getItem(`branding_${parentWhitelabelId}`) : null)
      if (cached) {
        const parsed = JSON.parse(cached) as CompanyBranding
        applyBrandingCssVariables(parsed)
        return parsed
      }
    } catch {
      // Ignora erro de JSON parse
    }
    return null
  })

  const [loading, setLoading] = useState<boolean>(() => {
    if (!companyId) return false
    if (typeof window !== 'undefined') {
      const hasCached = sessionStorage.getItem(`branding_${companyId}`) ||
        (parentWhitelabelId ? sessionStorage.getItem(`branding_${parentWhitelabelId}`) : null)
      if (hasCached) {
        return false
      }
    }
    return true
  })

  const brandingRef = useRef<CompanyBranding | null>(branding)
  brandingRef.current = branding

  const fetchBranding = useCallback(async (targetCompanyId: string, parentId?: string | null) => {
    try {
      const data = await brandingService.get(targetCompanyId)
      setBranding(data)
      applyBrandingCssVariables(data)
      if (typeof window !== 'undefined') {
        sessionStorage.setItem(`branding_${targetCompanyId}`, JSON.stringify(data))
        if (parentId) {
          sessionStorage.setItem(`branding_${parentId}`, JSON.stringify(data))
        }
      }
      return data
    } catch (err) {
      if (import.meta.env.MODE !== 'test') {
        console.error('[useBranding] Erro ao carregar branding:', err)
      }
      return null
    } finally {
      setLoading(false)
    }
  }, [])

  const refresh = useCallback(async () => {
    if (!companyId) return
    setLoading(true)
    await fetchBranding(companyId, parentWhitelabelId)
  }, [companyId, parentWhitelabelId, fetchBranding])

  useEffect(() => {
    if (!companyId) {
      setBranding(null)
      setLoading(false)
      removeBrandingCssVariables()
      return
    }

    let isMounted = true

    // Tenta carregar do cache para resposta instantânea
    let hasCache = false
    try {
      const cached = sessionStorage.getItem(`branding_${companyId}`) ||
        (parentWhitelabelId ? sessionStorage.getItem(`branding_${parentWhitelabelId}`) : null)
      if (cached) {
        const parsed = JSON.parse(cached) as CompanyBranding
        if (isMounted) {
          setBranding(parsed)
          applyBrandingCssVariables(parsed)
          hasCache = true
          setLoading(false)
        }
      }
    } catch {
      // Ignore
    }

    if (!hasCache) {
      setLoading(true)
    }

    void fetchBranding(companyId, parentWhitelabelId)

    // Observa mutações em data-theme para re-aplicar variáveis customizadas se o tema for alternado
    const targetNode = document.documentElement
    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-theme') {
          if (brandingRef.current) {
            applyBrandingCssVariables(brandingRef.current)
          }
        }
      }
    })

    observer.observe(targetNode, { attributes: true, attributeFilter: ['data-theme'] })

    return () => {
      isMounted = false
      observer.disconnect()
    }
  }, [companyId, parentWhitelabelId, fetchBranding])

  return {
    branding,
    loading,
    refresh,
  }
}
